var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { Platform, Text as RNText, StyleSheet, View, } from 'react-native';
import { theme } from '@xbto/design-system';
import { createBox } from '../../theme';
const TextBox = createBox(RNText);
const compactStyle = {
    // TODO(Hadrien): Fix for mobile : SH-12776
    lineHeight: '100%',
    textBoxTrim: 'trim-both',
    textBoxEdge: 'cap alphabetic',
};
// Do not expose TextBase
// Use it to build other "Text" components
function TextBase(_a) {
    var { center = false, 
    // Color must either be passed as prop or as style
    color: colorName, em = false, xem = false, lhFull = false, style } = _a, rest = __rest(_a, ["center", "color", "em", "xem", "lhFull", "style"]);
    // Vars
    const hasEmphasis = em || xem;
    const flatStyle = StyleSheet.flatten(style);
    const styleColor = flatStyle === null || flatStyle === void 0 ? void 0 : flatStyle.color;
    const styleFontSize = flatStyle === null || flatStyle === void 0 ? void 0 : flatStyle.fontSize;
    const defaultColorName = hasEmphasis ? 'primary' : 'secondary';
    // Render
    return (_jsx(TextBox, Object.assign({}, rest, { style: [
            style,
            em && $.weightMedium,
            xem && $.weightSemiBold,
            styleColor
                ? { color: styleColor }
                : { color: theme.color.text[colorName || defaultColorName] },
            lhFull &&
                (Platform.OS === 'web'
                    ? compactStyle
                    : { lineHeight: styleFontSize }),
            center && $.center,
        ] })));
}
export function TextBody(_a) {
    var { small = false, large = false, style } = _a, rest = __rest(_a, ["small", "large", "style"]);
    return (_jsx(TextBase, Object.assign({}, rest, { style: [$.body, small && $.bodySmall, large && $.bodyLarge, style] })));
}
export function TextLabel(_a) {
    var { style } = _a, rest = __rest(_a, ["style"]);
    return _jsx(TextBase, Object.assign({}, rest, { style: [$.label, style] }));
}
export function TextDisplay(_a) {
    var { small = false, style } = _a, rest = __rest(_a, ["small", "style"]);
    return (_jsx(TextBase, Object.assign({ color: "primary" }, rest, { style: [$.display, small && $.displaySmall, style] })));
}
export function TextHeadline(_a) {
    var { style } = _a, rest = __rest(_a, ["style"]);
    return _jsx(TextBase, Object.assign({}, rest, { style: [$.headline, style] }));
}
export function TextTitle(_a) {
    var { small = false, large = false, style } = _a, rest = __rest(_a, ["small", "large", "style"]);
    return (_jsx(TextBase, Object.assign({ color: "primary" }, rest, { style: [$.title, small && $.titleSmall, large && $.titleLarge, style] })));
}
// This is useful to create "a line of text": <TextParagraph><Text /> <Text /></TextParagraph>
export { TextBox as TextParagraph };
export const TextUnderline = (_a) => {
    var { color: colorName = 'secondary' } = _a, rest = __rest(_a, ["color"]);
    return (_jsx(View, { style: [$.underline, { borderBottomColor: theme.color.text[colorName] }], children: _jsx(TextBody, Object.assign({ em: true }, rest, { color: colorName })) }));
};
const fontFamilies = {
    InterWeb: { fontFamily: 'Inter' },
    InterRegular: { fontFamily: 'Inter-Regular' },
    InterMedium: { fontFamily: 'Inter-Medium' },
    InterSemiBold: { fontFamily: 'Inter-SemiBold' },
};
const fontNormalStyle = Platform.select({
    android: Object.assign({}, fontFamilies.InterRegular),
    ios: Object.assign({}, fontFamilies.InterRegular),
    web: Object.assign(Object.assign({}, fontFamilies.InterWeb), { fontWeight: '400' }),
});
export const fontMediumStyle = Platform.select({
    android: Object.assign({}, fontFamilies.InterMedium),
    ios: Object.assign({}, fontFamilies.InterMedium),
    web: Object.assign(Object.assign({}, fontFamilies.InterWeb), { fontWeight: '500' }),
});
const fontSemiBoldStyle = Platform.select({
    android: Object.assign({}, fontFamilies.InterSemiBold),
    ios: Object.assign({}, fontFamilies.InterSemiBold),
    web: Object.assign(Object.assign({}, fontFamilies.InterWeb), { fontWeight: '600' }),
});
const fontWeights = {
    normal: fontNormalStyle,
    medium: fontMediumStyle,
    semiBold: fontSemiBoldStyle,
};
export const TextBodySmall = {
    fontSize: Platform.select({
        web: theme.fontsize.xs,
        default: theme.fontsize['2xs'],
    }),
    lineHeight: Platform.select({
        web: theme.lineheight['lh-2'],
        default: theme.lineheight['lh-1'],
    }),
};
// Do not set color in any class
// TODO(Hadrien): Block color prop through TS or ESLint something like Omit<RNTextStyle, 'color'>
const $ = StyleSheet.create({
    body: Object.assign(Object.assign({}, fontWeights.normal), { fontSize: Platform.select({
            web: theme.fontsize.sm,
            default: theme.fontsize.xs,
        }), lineHeight: Platform.select({
            web: theme.lineheight['lh-3'],
            default: theme.lineheight['lh-2'],
        }) }),
    bodySmall: TextBodySmall,
    bodyLarge: {
        fontSize: theme.fontsize.base,
        lineHeight: theme.lineheight['lh-4'],
    },
    label: Object.assign(Object.assign({}, fontWeights.normal), { fontSize: theme.fontsize['2xs'], lineHeight: theme.lineheight['lh-1'] }),
    headline: Object.assign(Object.assign({}, fontWeights.medium), { fontSize: theme.fontsize.xl, lineHeight: theme.lineheight['lh-4'] }),
    title: Object.assign(Object.assign({}, fontWeights.medium), { fontSize: Platform.select({
            web: theme.fontsize.md,
            default: theme.fontsize.base,
        }), lineHeight: Platform.select({
            web: theme.lineheight['lh-5'],
            default: theme.lineheight['lh-4'],
        }) }),
    titleSmall: {
        fontSize: theme.fontsize.base,
        lineHeight: theme.lineheight['lh-4'],
    },
    titleLarge: {
        fontSize: theme.fontsize.lg,
        lineHeight: theme.lineheight['lh-6'],
    },
    weightMedium: Object.assign({}, fontWeights.medium),
    weightSemiBold: Object.assign({}, fontWeights.semiBold),
    underline: {
        borderBottomWidth: 1,
        borderBottomColor: theme.color.border.tertiary,
    },
    display: Object.assign(Object.assign({}, fontWeights.medium), { fontSize: theme.fontsize['2xl'], lineHeight: theme.lineheight['lh-8'] }),
    displaySmall: Object.assign(Object.assign({}, fontWeights.medium), { fontSize: theme.fontsize.xl, lineHeight: theme.lineheight['lh-7'] }),
    center: {
        textAlign: 'center',
    },
});
